// LiveTrackingPage.jsx
import React, { useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import ReactDOM from 'react-dom'
import { Icon } from '@iconify/react/dist/iconify.js'; // Updated import
import axios from 'axios';
import 'firebase/app';
import { ref, onValue, off } from 'firebase/database';
import { db, API_URL } from '..';
import MasterLayout from '../masterLayout/MasterLayout';
import Breadcrumb from '../components/Breadcrumb';
import mapboxgl from 'mapbox-gl';
import 'mapbox-gl/dist/mapbox-gl.css';

mapboxgl.accessToken = 'pk.eyJ1IjoiemFtcCIsImEiOiJjbTkxa3dkcnMwMmQxMnJzYzBobW9hOHBiIn0.GWvvqPrWqtMby0HYCYdjMQ';

// Utility function to validate coordinates
const isValidCoords = (coords) => {
  return (
    coords &&
    typeof coords.lng === 'number' &&
    typeof coords.lat === 'number' &&
    !isNaN(coords.lng) &&
    !isNaN(coords.lat)
  );
};

const LiveTrackingPage = () => {
  const { id } = useParams();

  return (
    <MasterLayout>
      <Breadcrumb title={'Job Tracking'} />
      {id ? <LiveTrackingScreen jobId={id} /> : <div>No job ID provided</div>}
    </MasterLayout>
  );
};

const LiveTrackingScreen = ({ jobId }) => {
  const mapContainerRef = useRef(null);
  const mapRef = useRef(null);
  const [jobData, setJobData] = useState(null);
  const [pickupCoords, setPickupCoords] = useState(null);
  const [dropoffCoords, setDropoffCoords] = useState(null);
  const [trackingCoords, setTrackingCoords] = useState([]);
  const [truckPosition, setTruckPosition] = useState(null);
  const [eta, setEta] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [zoomLevel, setZoomLevel] = useState(12);
  const markersRef = useRef({
    pickup: null,
    dropoff: null,
    truck: null,
  });

  // Initialize Map
  useEffect(() => {
    let intervalId;

    const initializeMap = () => {
      if (!mapContainerRef.current) {
        console.log('Container ref not ready, retrying...');
        return false;
      }

      try {
        if (mapRef.current) {
          console.log('Map already initialized');
          return true;
        }

        const container = mapContainerRef.current;
        console.log('Container dimensions:', {
          width: container.offsetWidth,
          height: container.offsetHeight,
        });

        if (!container.offsetWidth || !container.offsetHeight) {
          throw new Error('Map container has no dimensions');
        }

        mapRef.current = new mapboxgl.Map({
          container: mapContainerRef.current,
          style: 'mapbox://styles/mapbox/streets-v11',
          center: [0, 0],
          zoom: zoomLevel,
        });

        mapRef.current.on('load', () => {
          console.log('Map loaded successfully');
        });

        mapRef.current.on('error', (e) => {
          console.error('Map error:', e);
        });

        return true;
      } catch (err) {
        console.error('Map initialization failed:', err);
        setError('Failed to initialize map: ' + err.message);
        return false;
      }
    };

    if (!initializeMap()) {
      intervalId = setInterval(() => {
        if (initializeMap()) {
          clearInterval(intervalId);
        }
      }, 100);
    }

    return () => {
      if (intervalId) clearInterval(intervalId);
      if (mapRef.current) {
        mapRef.current.remove();
        mapRef.current = null;
      }
    };
  }, []);

  // Fetch Job Data
  useEffect(() => {
    const fetchJobData = async () => {
      try {
        setLoading(true);
        const response = await axios.get(`${API_URL}/api/job/affiliate/fetch-single/${jobId}`);
        const data = response.data.data;
        setJobData(data);

        const pickupMarker = data?.address_marker?.split(',').map(Number);
        const dropoffMarker = data?.dropoff_address_marker?.split(',').map(Number);

        if (pickupMarker && pickupMarker.length === 2 && !pickupMarker.some(isNaN)) {
          setPickupCoords({ lat: pickupMarker[0], lng: pickupMarker[1] });
        } else {
          console.warn('Invalid pickup coordinates:', data?.address_marker);
        }

        if (dropoffMarker && dropoffMarker.length === 2 && !dropoffMarker.some(isNaN)) {
          setDropoffCoords({ lat: dropoffMarker[0], lng: dropoffMarker[1] });
          setupFirebaseListener({ lat: dropoffMarker[0], lng: dropoffMarker[1] }, data.status);
        } else {
          console.warn('Invalid dropoff coordinates:', data?.dropoff_address_marker);
        }
      } catch (err) {
        console.error('Error fetching job data:', err);
        setError(err.message || 'Failed to load job data');
      } finally {
        setLoading(false);
      }
    };

    fetchJobData();

    return () => {
      const trackingRef = ref(db, `/tracking/${jobId}/location`);
      off(trackingRef);
    };
  }, [jobId]);

  const setupFirebaseListener = (dropoff, status) => {
    const trackingRef = ref(db, `/tracking/${jobId}/location`);

    onValue(trackingRef, async (snapshot) => {
      try {
        const data = snapshot.val();
        if (data) {
          const routeCoords = Object.values(data)
            .map((loc) => ({
              lng: loc.longitude,
              lat: loc.latitude,
              timestamp: loc.timestamp,
            }))
            .filter((coord) => isValidCoords(coord))
            .sort((a, b) => a.timestamp - b.timestamp);

          setTrackingCoords((prev) => {
            const uniqueNewCoords = routeCoords.filter(
              (newCoord) =>
                !prev.some(
                  (prevCoord) =>
                    prevCoord.lng === newCoord.lng && prevCoord.lat === newCoord.lat
                )
            );
            return [...prev, ...uniqueNewCoords];
          });
          console.log(routeCoords);
          if (routeCoords.length > 0) {
            const latestCoord = routeCoords[routeCoords.length - 1];
            setTruckPosition([latestCoord.lng, latestCoord.lat]);

            if (isValidCoords(dropoff) && status !== 'completed') {
              await calculateETA([latestCoord.lng, latestCoord.lat], [dropoff.lng, dropoff.lat]);
            } else {
              setEta('Completed');
            }
          }
        }
      } catch (err) {
        setError(err.message || 'Failed to process tracking data');
      }
    });
  };

  const calculateETA = async (currentPosition, destination) => {
    try {
      if (
        !currentPosition.every((num) => typeof num === 'number' && !isNaN(num)) ||
        !destination.every((num) => typeof num === 'number' && !isNaN(num))
      ) {
        throw new Error('Invalid coordinates for ETA calculation');
      }
      const url = `https://api.mapbox.com/directions/v5/mapbox/driving/${currentPosition[0]},${currentPosition[1]};${destination[0]},${destination[1]}?geometries=geojson&access_token=${mapboxgl.accessToken}`;
      const response = await axios.get(url);
      if (response.data.routes && response.data.routes.length > 0) {
        const duration = response.data.routes[0].duration;
        setEta(Math.round(duration / 60));
      }
    } catch (err) {
      setError('Failed to calculate ETA: ' + err.message);
    }
  };

  const fitToRoute = () => {
    if (!mapRef.current) return;

    const bounds = new mapboxgl.LngLatBounds();
    let hasValidBounds = false;

    if (isValidCoords(pickupCoords)) {
      bounds.extend([pickupCoords.lng, pickupCoords.lat]);
      hasValidBounds = true;
    }
    if (isValidCoords(dropoffCoords)) {
      bounds.extend([dropoffCoords.lng, dropoffCoords.lat]);
      hasValidBounds = true;
    }
    if (truckPosition) {
      bounds.extend(truckPosition);
      hasValidBounds = true;
    }

    if (hasValidBounds) {
      mapRef.current.fitBounds(bounds, { padding: 100, duration: 1000 });
    }
  };

  const handleZoomIn = () => {
    if (!mapRef.current) return;
    const newZoom = Math.min(zoomLevel + 1, 20);
    setZoomLevel(newZoom);
    mapRef.current.zoomTo(newZoom, { duration: 200 });
  };

  const handleZoomOut = () => {
    if (!mapRef.current) return;
    const newZoom = Math.max(zoomLevel - 1, 0);
    setZoomLevel(newZoom);
    mapRef.current.zoomTo(newZoom, { duration: 200 });
  };

  const handleRefresh = () => {
    setTrackingCoords([]);
    setTruckPosition(null);
    setEta(null);
    setError(null);
  };

  // Update Map Features (Markers and Polyline)
  useEffect(() => {
    if (!mapRef.current) return;

    const addOrUpdateMarker = (type, coords, icon, color) => {
      if (!isValidCoords(coords)) return;

      const el = document.createElement('div');
      el.className = `marker ${type}-marker`;
      // Use ReactDOM to render Iconify icon to the DOM element
      const iconElement = (
        <Icon icon={icon} style={{ color, fontSize: '30px' }} />
      );
      ReactDOM.render(iconElement, el);

      if (markersRef.current[type]) {
        markersRef.current[type].setLngLat([coords.lng, coords.lat]);
      } else {
        markersRef.current[type] = new mapboxgl.Marker(el)
          .setLngLat([coords.lng, coords.lat])
          .addTo(mapRef.current);
      }
    };

    // Add markers
    addOrUpdateMarker('pickup', pickupCoords, 'mdi:map-marker', '#F44336'); // Red for pickup
    addOrUpdateMarker('dropoff', dropoffCoords, 'mdi:map-marker-check', '#4CAF50'); // Green for dropoff
    if (truckPosition && truckPosition.every((num) => typeof num === 'number' && !isNaN(num))) {
      addOrUpdateMarker('truck', { lng: truckPosition[0], lat: truckPosition[1] }, 'mdi:truck', '#3F51B5'); // Blue for truck
    }

    // Add polyline (Pickup -> Truck -> Dropoff)
    const routeCoords = [];
    if (isValidCoords(pickupCoords)) routeCoords.push([pickupCoords.lng, pickupCoords.lat]);
    if (truckPosition) routeCoords.push(truckPosition);
    if (isValidCoords(dropoffCoords)) routeCoords.push([dropoffCoords.lng, dropoffCoords.lat]);

    if (routeCoords.length > 1) {
      const geojson = {
        type: 'Feature',
        geometry: {
          type: 'LineString',
          coordinates: routeCoords,
        },
      };

      if (!mapRef.current.getSource('route')) {
        mapRef.current.addSource('route', {
          type: 'geojson',
          data: geojson,
        });
        mapRef.current.addLayer({
          id: 'route',
          type: 'line',
          source: 'route',
          layout: { 'line-join': 'round', 'line-cap': 'round' },
          paint: { 'line-color': '#FF0000', 'line-width': 4 },
        });
      } else {
        mapRef.current.getSource('route').setData(geojson);
      }
    }

    fitToRoute();
  }, [pickupCoords, dropoffCoords, truckPosition]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex flex-col items-center justify-center h-screen text-red-500">
        <Icon icon="mdi:alert-circle" className="text-4xl mb-4" />
        <p className="text-xl mb-4">{error}</p>
        <button
          onClick={handleRefresh}
          className="px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600"
        >
          Retry
        </button>
      </div>
    );
  }

  return (
    <div className="relative w-full h-screen">
          <div style={{position: 'absolute',zIndex: 3000,paddingBottom: 10}}>
            <div style={{backgroundColor:'white'}}>

        <button onClick={handleZoomIn} className="px-3 py-2 hover:bg-gray-100 rounded-t-md">
          <Icon icon="mdi:plus" />
        </button>
        <div className="border-t border-gray-200"></div>
        <button onClick={handleZoomOut} className="px-3 py-2 hover:bg-gray-100">
          <Icon icon="mdi:minus" />
        </button>
        <div className="border-t border-gray-200"></div>
        <button onClick={fitToRoute} className="px-3 py-2 hover:bg-gray-100 rounded-b-md">
          <Icon icon="mdi:fit-to-page" />
        </button>
            </div>
      </div>
      <div
        ref={mapContainerRef}
        className="absolute top-0 left-0 w-full h-full"
        style={{ minHeight: '400px' }}
      />
      {/* Debug Info */}
    

    

      <div className="absolute bottom-4 left-4 right-4 bg-white rounded-lg shadow-xl z-10 p-4 max-w-md">
        <div className="space-y-3">
          {eta !== null && (
            <div className="flex items-center gap-2 text-gray-800">
              <Icon icon="mdi:clock-outline" fontSize={18} className="text-yellow-500" />
              <span >{eta === 'Completed' ? 'Completed' : `ETA: ${eta} minutes`}</span>
            </div>
          )}
          <div className="flex items-center gap-2 text-sm text-gray-700">
            <Icon icon="mdi:map-marker" fontSize={22} className="text-red-500" />
            <span>{jobData?.address || 'Loading...'}</span>
          </div>
          <div className="flex items-center gap-2 text-sm text-gray-700">
            <Icon icon="mdi:map-marker-check" fontSize={22} className="text-green-500" />
            <span>{jobData?.dropoff_address || 'Loading...'}</span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LiveTrackingPage;